import { useCallback, useEffect, useRef, useState } from 'react'
import { DateRangePicker, defaultStaticRanges, defaultInputRanges, createStaticRanges } from 'react-date-range'
import { addDays, endOfWeek, format, startOfToday, startOfWeek } from 'date-fns'
import { Alert, Button, Dropdown, Spinner } from 'react-bootstrap'
import { Range, RangeKeyDict } from 'react-date-range'
import { enGB } from 'date-fns/locale'
import { FiCheck, FiChevronDown, FiStar, FiX } from 'react-icons/fi'
import { useAppContext } from '../../../../AppContext'
import { savePref, loadPref, resolveLabel } from './date-range-preference'
import './date-range-selector.css'

const MOBILE_BREAKPOINT = '(max-width: 767px)'
const COMPACT_BREAKPOINT = '(max-width: 1024px)'
const COMPACT_MENU_GAP = 8

export function getCompactMenuOffset({ reference, popper }: { reference: { height: number }, popper: { height: number } }) {
    return [(reference.height - popper.height) / 2, COMPACT_MENU_GAP] as [number, number]
}

const compactMenuPopperConfig = {
    modifiers: [{
        name: 'offset',
        options: { offset: getCompactMenuOffset },
    }],
}

const cloneRange = (range: Range): Range => ({
    ...range,
    startDate: range.startDate ? new Date(range.startDate) : undefined,
    endDate: range.endDate ? new Date(range.endDate) : undefined,
    key: range.key || 'selection'
})

const clampRangeToDate = (range: Range, maximumDate: Date): Range => {
    const clampDate = (date?: Date) => date && date > maximumDate ? new Date(maximumDate) : date
    return {
        ...range,
        startDate: clampDate(range.startDate),
        endDate: clampDate(range.endDate),
        key: range.key || 'selection'
    }
}

export default function DateRangeSelector(opts: {
    onSubmit: (startDate: Date, endDate: Date) => Promise<void>
    pageKey?: string
    defaultLabel?: string
    submitInitialRange?: boolean
    appearance?: 'default' | 'toolbar'
    compactMenu?: boolean
}) {
    const appContext = useAppContext()
    const onRangeSubmit = opts.onSubmit
    const today = startOfToday()

    const getFromAndToDefault = () => {
        if(appContext.isPdf && appContext.fromDate && appContext.toDate) {
            const range = clampRangeToDate({
                startDate: new Date(appContext.fromDate),
                endDate: new Date(appContext.toDate),
                key: 'selection'
            }, today)
            return { fromDate: range.startDate!, toDate: range.endDate! }
        }

        if (opts.pageKey) {
            const saved = resolveLabel(loadPref(opts.pageKey))
            if (saved) {
                const range = clampRangeToDate({ ...saved, key: 'selection' }, today)
                return { fromDate: range.startDate!, toDate: range.endDate! }
            }
        }

        if (opts.defaultLabel) {
            const defaultRange = resolveLabel(opts.defaultLabel)
            if (defaultRange) {
                const range = clampRangeToDate({ ...defaultRange, key: 'selection' }, today)
                return { fromDate: range.startDate!, toDate: range.endDate! }
            }
        }

        return {fromDate: addDays(new Date(), -30), toDate: new Date()}
    }
    const { fromDate, toDate } = getFromAndToDefault()

    const [chosenRange, setChosenRange] = useState<Range[]>([{
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [tempRange, setTempRange] = useState<Range[]>([{
        // startDate: addDays(new Date(), -30),
        // endDate: new Date(),
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [activePillLabel, setActivePillLabel] = useState<string | null>(null)
    const [savedDefault, setSavedDefault] = useState<string | null>(
        opts.pageKey ? (loadPref(opts.pageKey) ?? opts.defaultLabel ?? '30 Days') : null
    )
    const hasSubmittedInitialRange = useRef(false)
    const [isMobile, setIsMobile] = useState<boolean>(() => (
        typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_BREAKPOINT).matches
    ))
    const [isCompact, setIsCompact] = useState<boolean>(() => (
        typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(COMPACT_BREAKPOINT).matches
    ))

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

        const mobileQuery = window.matchMedia(MOBILE_BREAKPOINT)
        const compactQuery = window.matchMedia(COMPACT_BREAKPOINT)
        const updateMobileViewport = (event: MediaQueryListEvent | MediaQueryList) => setIsMobile(event.matches)
        const updateCompactViewport = (event: MediaQueryListEvent | MediaQueryList) => setIsCompact(event.matches)

        updateMobileViewport(mobileQuery)
        updateCompactViewport(compactQuery)
        mobileQuery.addEventListener?.('change', updateMobileViewport)
        compactQuery.addEventListener?.('change', updateCompactViewport)
        return () => {
            mobileQuery.removeEventListener?.('change', updateMobileViewport)
            compactQuery.removeEventListener?.('change', updateCompactViewport)
        }
    }, [])

    const mondayWeekStart = { weekStartsOn: 1 as 1, locale: enGB }

    const remapStaticRange = (rangeObj: any) => {
        return {
            ...rangeObj,
            range: () => {
                const r = rangeObj.range()

                if (rangeObj.label.toLowerCase().includes('week')) {
                    const baseDate = rangeObj.label.toLowerCase().includes('last')
                        ? addDays(new Date(), -7)
                        : new Date()

                    return clampRangeToDate({
                        startDate: startOfWeek(baseDate, mondayWeekStart),
                        endDate: endOfWeek(baseDate, mondayWeekStart),
                        key: r.key || 'selection'
                    }, today)
                }

                return clampRangeToDate(r, today)
            }
        }
    }

    const mondayStaticRanges = defaultStaticRanges.map(remapStaticRange)

    const customRanges = [
        { label: 'Last 7 Days', num: 7 },
        { label: 'Last 14 Days', num: 14 },
        { label: '30 Days', num: 30 },
        { label: '60 Days', num: 60 },
        { label: '90 Days', num: 90 },
    ].map(({ label, num }) => ({
        label,
        range: () => ({
            startDate: addDays(new Date(), -num),
            endDate: addDays(new Date(), -1),
            key: 'selection'
        })
    }))

    const allRangeDefinitions = [...mondayStaticRanges, ...customRanges]

    const customStaticRanges = createStaticRanges(
        allRangeDefinitions.map(r => ({ ...r, hasCustomRendering: Boolean(opts.pageKey) }))
    )


    const customInputRanges = defaultInputRanges.filter(
        (range: any) => !['days up to today', 'days starting today'].includes(range.label)
    )

    const handleSelect = (ranges: RangeKeyDict) => {
        setTempRange([clampRangeToDate(ranges.selection, today)])
        setActivePillLabel(null)
    }

    const formatDateRange = (startDate?: Date, endDate?: Date): string => {
        if (!startDate || !endDate) return ''
        if (startDate.getFullYear() === endDate.getFullYear()) {
            return `${format(startDate, 'd MMM')} – ${format(endDate, 'd MMM yyyy')}`
        }
        return `${format(startDate, 'd MMM yyyy')} – ${format(endDate, 'd MMM yyyy')}`
    }

    const resetDraftRange = () => {
        setTempRange(chosenRange.map(cloneRange))
        setActivePillLabel(null)
        setSubmitError(null)
    }

    const handleToggle = (nextShow: boolean) => {
        if (isLoading) return
        if (nextShow) resetDraftRange()
        setIsOpen(nextShow)
    }

    const handleCancel = () => {
        resetDraftRange()
        setIsOpen(false)
    }

    const submitRange = useCallback(async (range: Range[], closeOnSuccess: boolean) => {
        const startDate0 = range[0]?.startDate
        const endDate0 = range[0]?.endDate
        if (!startDate0 || !endDate0) return

        setIsLoading(true)
        setSubmitError(null)

        try {
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve())
            })

            const startDate = new Date(startDate0)
            startDate.setHours(0, 0, 0, 0)
            const endDate = new Date(endDate0)
            endDate.setHours(0, 0, 0, 0)

            await onRangeSubmit(startDate, endDate)
            setChosenRange(range.map(cloneRange))
            if (closeOnSuccess) setIsOpen(false)
        } catch (error) {
            console.error('Failed to apply date range:', error)
            setSubmitError('The date range could not be applied. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }, [onRangeSubmit])

    useEffect(() => {
        if (!opts.submitInitialRange || hasSubmittedInitialRange.current) return
        hasSubmittedInitialRange.current = true
        void submitRange(chosenRange, false)
    }, [chosenRange, opts.submitInitialRange, submitRange])

    const onSubmit = () => submitRange(tempRange, true)

    const selectedDraftLabel = formatDateRange(tempRange[0]?.startDate, tempRange[0]?.endDate)

    return (
        <Dropdown
            className={`date-range-selector${opts.appearance === 'toolbar' ? ' date-range-selector--toolbar' : ''}${opts.compactMenu ? ' date-range-selector--compact-menu' : ''}`}
            drop={opts.compactMenu ? 'end' : undefined}
            show={isOpen}
            onToggle={handleToggle}
            autoClose={isLoading ? false : 'outside'}
        >
            <Dropdown.Toggle 
                className={`date-range-selector__toggle${opts.appearance === 'toolbar' ? ' btn-sm btn-light dropdown-as-text' : ''}`}
                aria-label={`Choose date range. Current range: ${formatDateRange(chosenRange[0].startDate, chosenRange[0].endDate)}`}
            >
                <span className="date-range-selector__toggle-value">
                    {formatDateRange(chosenRange[0].startDate, chosenRange[0].endDate)}
                </span>
                <FiChevronDown className="date-range-selector__chevron" aria-hidden="true" />
            </Dropdown.Toggle>

            <Dropdown.Menu
                show={isOpen}
                align={opts.compactMenu ? 'start' : 'end'}
                flip={opts.compactMenu ? false : undefined}
                popperConfig={opts.compactMenu ? compactMenuPopperConfig : undefined}
                className="date-range-selector__menu"
                renderOnMount
            >
                <div className="date-range-selector__menu-header">
                    <div>
                        <span className="date-range-selector__eyebrow">Date range</span>
                        <strong>{selectedDraftLabel}</strong>
                    </div>
                    <button
                        type="button"
                        className="date-range-selector__close"
                        onClick={handleCancel}
                        disabled={isLoading}
                        aria-label="Close date picker"
                    >
                        <FiX />
                    </button>
                </div>
                {isMobile && (
                    <div className="date-range-selector__quick-ranges" aria-label="Quick date ranges">
                        {allRangeDefinitions.map((rangeObj: any) => {
                            const label: string = rangeObj.label
                            const isActive = label === activePillLabel
                            const isDefault = label === savedDefault
                            return (
                                <div className={`date-range-selector__quick-range${isActive ? ' is-active' : ''}`}
                                    key={label}
                                >
                                    <button
                                        type="button"
                                        className="date-range-selector__quick-range-label"
                                        onClick={() => {
                                            const r = clampRangeToDate(rangeObj.range(), today)
                                            setTempRange([{ ...r, key: 'selection' }])
                                            setActivePillLabel(label)
                                        }}
                                    >
                                        {isActive && <FiCheck aria-hidden="true" />}
                                        <span>{label}</span>
                                    </button>
                                    {opts.pageKey && (
                                        <button
                                            type="button"
                                            className={`date-range-selector__save-default${isDefault ? ' is-default' : ''}`}
                                            title={isDefault ? 'Saved as default' : 'Save as default'}
                                            aria-label={isDefault ? `${label} is the default range` : `Save ${label} as the default range`}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (opts.pageKey) {
                                                    savePref(opts.pageKey, label)
                                                    setSavedDefault(label)
                                                    const r = clampRangeToDate(rangeObj.range(), today)
                                                    setTempRange([{ ...r, key: 'selection' }])
                                                    setActivePillLabel(label)
                                                }
                                            }}
                                        >
                                            <FiStar fill={isDefault ? 'currentColor' : 'none'} />
                                        </button>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
                <DateRangePicker
                    onChange={handleSelect}
                    moveRangeOnFirstSelection={false}
                    months={opts.compactMenu || isCompact ? 1 : 2}
                    ranges={tempRange}
                    showDateDisplay={!opts.compactMenu}
                    staticRanges={isMobile || opts.compactMenu ? [] : customStaticRanges}
                    inputRanges={isMobile || opts.compactMenu ? [] : customInputRanges}
                    direction="horizontal"
                    maxDate={today}
                    locale={enGB}
                    renderStaticRangeLabel={opts.pageKey ? (rangeObj: any) => {
                        const label: string = rangeObj.label
                        const isDefault = label === savedDefault
                        return (
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <span>{label}</span>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label={isDefault ? `${label} is the default range` : `Save ${label} as the default range`}
                                    title={isDefault ? 'Saved as default' : 'Save as default'}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (opts.pageKey) {
                                            savePref(opts.pageKey, label)
                                            setSavedDefault(label)
                                            const resolved = resolveLabel(label)
                                            if (resolved) {
                                                setTempRange([clampRangeToDate({ ...resolved, key: 'selection' }, today)])
                                            }
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') return
                                        e.preventDefault()
                                        e.stopPropagation()
                                        if (opts.pageKey) {
                                            savePref(opts.pageKey, label)
                                            setSavedDefault(label)
                                            const resolved = resolveLabel(label)
                                            if (resolved) {
                                                setTempRange([clampRangeToDate({ ...resolved, key: 'selection' }, today)])
                                            }
                                        }
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: '0 0 0 6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        lineHeight: 1,
                                        color: isDefault ? '#f5a623' : '#bbb',
                                        flexShrink: 0
                                    }}
                                >
                                    {isDefault ? '★' : '☆'}
                                </span>
                            </span>
                        )
                    } : undefined}
                />
                {submitError && (
                    <Alert variant="danger" className="date-range-selector__error" role="alert">
                        {submitError}
                    </Alert>
                )}
                <div className="date-range-selector__footer">
                    <span className="date-range-selector__footer-range">{selectedDraftLabel}</span>
                    <div className="date-range-selector__actions">
                        <Button variant="light" size="sm" onClick={handleCancel} disabled={isLoading}>
                            Cancel
                        </Button>
                        <Button size="sm" onClick={onSubmit} disabled={isLoading || !tempRange[0]?.startDate || !tempRange[0]?.endDate}>
                            {isLoading ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    Applying…
                                </>
                            ) : 'Apply range'}
                        </Button>
                    </div>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
